import { useQuery } from 'react-query';
import { DailyStats, DbUser, WorkoutData } from '../types';
import { fetchOuraData, fetchStravaWorkouts } from '../services/api';
import { activitiesToWorkouts, listRecentActivities } from '../services/activities';
import { fetchTerraData, mergeWorkouts } from '../services/terra';
import { fetchAppleHealthDay } from '../services/appleHealth';
import { getValidOuraToken, getValidStravaToken, supabase } from '../services/auth';
import { toLocalDayKey } from '../utils/date';

export const useStats = (date: Date = new Date()) => {
  const { data: session } = useQuery('session', async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  });

  const { data: user } = useQuery(
    ['user', session?.user?.id],
    async () => {
      if (!session?.user?.id) return null;
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .single();
      if (error) throw error;
      return data as DbUser;
    },
    {
      enabled: !!session?.user?.id,
    }
  );

  const { data: stats, isLoading } = useQuery<DailyStats>(
    ['daily-stats', toLocalDayKey(date), user?.id],
    async () => {
      if (!user) throw new Error('No user found');

      const stats: DailyStats = {};

      const terraData = user.terra_user_id
        ? await fetchTerraData(user.terra_user_id).catch(() => null)
        : null;

      if (user.oura_token) {
        const ouraToken = await getValidOuraToken(
          user.id,
          user.oura_token,
          user.oura_refresh_token ?? undefined,
          user.oura_token_expiry ?? undefined
        );
        const ouraData = ouraToken ? await fetchOuraData(ouraToken) : null;
        if (ouraData) {
          stats.oura = {
            sleep: ouraData.sleep,
            recovery: ouraData.recovery,
          };
        }
      }
      if (!stats.oura && terraData) {
        // Garmin (or other Terra wearable) fills the recovery card
        stats.oura = {
          sleep: terraData.sleep,
          recovery: terraData.recovery,
        };
      }
      if (!stats.oura) {
        // Apple Health relay (Health Auto Export) as the final source
        const appleDay = await fetchAppleHealthDay().catch(() => null);
        if (appleDay) {
          stats.oura = appleDay;
        }
      }

      // Strava activities and manual log entries merge into one training week
      let workouts: WorkoutData[] = [];
      if (user.strava_token) {
        const token = await getValidStravaToken(
          user.id,
          user.strava_token,
          user.strava_refresh_token ?? undefined,
          user.strava_token_expiry ?? undefined
        );
        workouts = await fetchStravaWorkouts(token);
      }
      const manual = await listRecentActivities().catch(() => []);
      workouts = mergeWorkouts(
        [...workouts, ...activitiesToWorkouts(manual)],
        terraData?.workouts ?? []
      );

      if (workouts.length > 0) {
        const withHr = workouts.filter((w) => w.averageHeartRate);
        stats.strava = {
          workouts,
          summary: {
            totalDuration: workouts.reduce((sum, w) => sum + w.duration, 0),
            totalDistance: workouts.reduce((sum, w) => sum + (w.distance || 0), 0),
            totalCalories: workouts.reduce((sum, w) => sum + (w.calories || 0), 0),
            averageHeartRate: withHr.length
              ? withHr.reduce((sum, w) => sum + (w.averageHeartRate || 0), 0) / withHr.length
              : undefined,
          },
        };
      }

      return stats;
    },
    {
      enabled: !!user,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
    }
  );

  return {
    stats,
    isLoading,
    hasOuraData: !!stats?.oura,
    hasStravaData: !!stats?.strava,
  };
};